import { onAuthStateChanged, type User } from "firebase/auth";
import { useEffect, useState } from "react";
import { auth } from "@/lib/infrastructure/auth";

// Sentinel value used when auth is disabled — satisfies the non-null user check
// in AuthGuard without touching Firebase.
const AUTH_DISABLED_USER = { uid: "auth-disabled" } as User;

export function useAuthState() {
  const [user, setUser] = useState<User | null>(() =>
    window.__VERDEREM_CONFIG__?.authDisabled ? AUTH_DISABLED_USER : null
  );
  // Loading stays true until either (a) we know auth is disabled, or
  // (b) Firebase's onAuthStateChanged has fired at least once.
  // Previously, this was `!window.__VERDEREM_CONFIG__`, which flipped loading
  // to false prematurely on production builds (config is always injected),
  // but that is harmless because the effect still drives the final state.
  const [loading, setLoading] = useState(
    () => !window.__VERDEREM_CONFIG__?.authDisabled
  );

  useEffect(() => {
    let mounted = true;
    let unsubscribe: (() => void) | undefined;

    const subscribeToFirebase = () => {
      if (!auth.app) {
        setLoading(false);
        return;
      }
      unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
        if (!mounted) {
          return;
        }
        setUser(firebaseUser);
        setLoading(false);
      });
    };

    // Fast path: runtime config was injected by the Fastify server
    // (production and most dev loads). We know the auth mode synchronously.
    if (window.__VERDEREM_CONFIG__) {
      if (window.__VERDEREM_CONFIG__.authDisabled) {
        setUser(AUTH_DISABLED_USER);
        setLoading(false);
      } else {
        // Auth is ENABLED — subscribe to Firebase so AuthGuard sees the
        // signed-in user. Without this, /campaigns redirects back to /login
        // in an infinite loop after a successful sign-in.
        subscribeToFirebase();
      }
    } else {
      // Slow path: config was not injected (Vite dev server serves the SPA
      // directly). Fetch it to decide between auth-disabled and Firebase.
      fetch("/api/config")
        .then((res) => res.json())
        .then((cfg: { authDisabled?: boolean }) => {
          if (!mounted) {
            return;
          }
          if (cfg.authDisabled) {
            setUser(AUTH_DISABLED_USER);
            setLoading(false);
          } else {
            subscribeToFirebase();
          }
        })
        .catch(() => {
          if (mounted) {
            // Config fetch failed — fall back to Firebase.
            subscribeToFirebase();
          }
        });
    }

    return () => {
      mounted = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  return { user, loading };
}
